// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * User Management Service
 * 
 * API client for user management operations:
 * - User CRUD operations
 * - Permission management
 * - Role management
 * - Tab access control
 */

import apiClient from '../config/api';

const userManagementService = {
  // User Management
  user: {
    // List users with filtering and pagination
    list: async (params = {}) => {
      const queryParams = new URLSearchParams();
      
      // Pagination
      if (params.skip !== undefined) queryParams.append('skip', params.skip);
      if (params.limit !== undefined) queryParams.append('limit', params.limit);
      
      // Filters
      if (params.search) queryParams.append('search', params.search);
      if (params.role_id) queryParams.append('role_id', params.role_id);
      if (params.company_id) queryParams.append('company_id', params.company_id);
      if (params.department_id) queryParams.append('department_id', params.department_id);
      if (params.include_inactive !== undefined) queryParams.append('include_inactive', params.include_inactive);
      if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
      
      const response = await apiClient.get(`/api/v1/users/?${queryParams.toString()}`);
      return response.data;
    },

    // Get user by ID
    get: async (userId) => {
      const response = await apiClient.get(`/api/v1/users/${userId}`);
      return response.data;
    },

    // Create new user
    create: async (userData) => {
      const response = await apiClient.post('/api/v1/users/', userData);
      return response.data;
    },

    // Update user
    update: async (userId, userData) => {
      const response = await apiClient.put(`/api/v1/users/${userId}`, userData);
      return response.data;
    },

    // Delete user (soft delete)
    delete: async (userId) => {
      const response = await apiClient.delete(`/api/v1/users/${userId}`);
      return response.data;
    },

    // Reset user password (generates temp password)
    resetPassword: async (userId, forceChange = true) => {
      const response = await apiClient.post(`/api/v1/users/${userId}/reset-password`, {
        force_change: forceChange
      });
      return response.data;
    },

    // Admin set user password (with specific password)
    adminChangePassword: async (userId, newPassword, forceChange = false) => {
      const response = await apiClient.post(`/api/v1/users/${userId}/admin-change-password`, {
        new_password: newPassword,
        force_change: forceChange
      });
      return response.data;
    },

    // Change user password (admin)
    changePassword: async (userId, passwordData) => {
      const response = await apiClient.put(`/api/v1/users/${userId}/change-password`, passwordData);
      return response.data;
    },

    // Unlock user account
    unlock: async (userId) => {
      const response = await apiClient.post(`/api/v1/users/${userId}/unlock`);
      return response.data;
    },

    // Get user context (with roles, permissions, etc.)
    getContext: async (userId) => {
      const response = await apiClient.get(`/api/v1/users/${userId}/context`);
      return response.data;
    },

    // Check if email exists (including soft-deleted users)
    checkEmail: async (email) => {
      const response = await apiClient.get(`/api/v1/users/check-email/${encodeURIComponent(email)}`);
      return response.data;
    },

    // Reactivate soft-deleted user
    reactivate: async (userId, passwordData) => {
      const response = await apiClient.post(`/api/v1/users/${userId}/reactivate`, passwordData);
      return response.data;
    }
  },

  // Permission Management
  permission: {
    // List all permissions
    list: async () => {
      const response = await apiClient.get('/api/v1/permissions/');
      return response.data;
    },

    // List all roles
    getRoles: async () => {
      const response = await apiClient.get('/api/v1/roles');
      return response.data;
    },

    // Get user permissions summary
    getUserPermissions: async (userId) => {
      const response = await apiClient.get(`/api/v1/permissions/user/${userId}`);
      return response.data;
    },

    // Grant user-specific permission
    grantUserPermission: async (userId, permissionData) => {
      const response = await apiClient.post(`/api/v1/permissions/user/${userId}/grant`, permissionData);
      return response.data;
    },

    // Revoke user-specific permission
    revokeUserPermission: async (userId, permissionId) => {
      const response = await apiClient.delete(`/api/v1/permissions/user/${userId}/revoke?permission_id=${permissionId}`);
      return response.data;
    }
  },

  // Tab Access Control
  tabAccess: {
    // Get accessible tabs for current user
    getMyTabs: async () => {
      const response = await apiClient.get('/api/v1/tabs/access/me');
      return response.data;
    },

    // Get accessible tabs for specific user (admin only)
    getUserTabs: async (userId) => {
      const response = await apiClient.get(`/api/v1/tabs/access/user/${userId}`);
      return response.data;
    },

    // Get tab access matrix for all roles (admin only)
    getTabMatrix: async () => {
      const response = await apiClient.get('/api/v1/tabs/access/matrix');
      return response.data;
    }
  },

  // Utility functions
  utils: {
    // Format user data for display
    formatUser: (user) => {
      return {
        ...user,
        displayName: user.full_name || user.username,
        statusText: user.is_active ? 'Active' : 'Inactive',
        lastLoginText: user.last_login_at 
          ? new Date(user.last_login_at).toLocaleDateString()
          : 'Never'
      };
    },

    // Format role data for display
    formatRole: (role) => {
      return {
        ...role,
        displayName: role.name,
        codeText: role.code,
        isSystemRole: role.is_system_role
      };
    },

    // Format permission data for display
    formatPermission: (permission) => {
      return {
        ...permission,
        displayName: permission.name,
        categoryText: permission.category.charAt(0).toUpperCase() + permission.category.slice(1),
        actionText: permission.action.charAt(0).toUpperCase() + permission.action.slice(1)
      };
    },

    // Check if user has permission
    hasPermission: (userPermissions, permissionCode) => {
      if (!userPermissions || !permissionCode) return false;
      
      // Check role permissions
      const roleHasPermission = userPermissions.permissions_by_category && 
        Object.values(userPermissions.permissions_by_category).some(category => 
          category.role_permissions?.some(perm => perm.code === permissionCode)
        );
      
      // Check user-specific overrides
      const userHasOverride = userPermissions.permissions_by_category && 
        Object.values(userPermissions.permissions_by_category).some(category => 
          category.user_overrides?.some(override => 
            override.permission.code === permissionCode && override.granted
          )
        );
      
      return roleHasPermission || userHasOverride;
    },

    // Get user's accessible tabs
    getAccessibleTabs: (tabAccess) => {
      if (!tabAccess || !tabAccess.tabs) return [];
      return tabAccess.tabs.filter(tab => tab.is_visible);
    },

    // Validate password strength
    validatePassword: (password) => {
      const errors = [];
      
      if (password.length < 8) {
        errors.push('Password must be at least 8 characters long');
      }
      
      if (!/[A-Z]/.test(password)) {
        errors.push('Password must contain at least one uppercase letter');
      }
      
      if (!/[a-z]/.test(password)) {
        errors.push('Password must contain at least one lowercase letter');
      }
      
      if (!/[0-9]/.test(password)) {
        errors.push('Password must contain at least one number');
      }
      
      if (!/[^A-Za-z0-9]/.test(password)) {
        errors.push('Password must contain at least one special character');
      }
      
      return {
        isValid: errors.length === 0,
        errors
      };
    }
  }
};

export default userManagementService;
